import { Dialog, DialogActions, DialogContent, DialogTitle } from "@connected-repo/ui-mui/feedback/Dialog";
import { Button } from "@connected-repo/ui-mui/form/Button";
import { TextField } from "@connected-repo/ui-mui/form/TextField";
import { Stack } from "@connected-repo/ui-mui/layout/Stack";
import { orpc } from "@superadmin/utils/orpc.client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import type React from "react";
import { useState } from "react";
import { toast } from "react-toastify";

interface GrantMinutesDialogProps {
	open: boolean;
	onClose: () => void;
	teamId: string;
	teamName: string;
}

/** Credit purchased minutes to a team (an invoice / order becomes a ledger row). */
export function GrantMinutesDialog({ open, onClose, teamId, teamName }: GrantMinutesDialogProps) {
	const queryClient = useQueryClient();
	const [minutes, setMinutes] = useState("");
	const [reference, setReference] = useState("");
	const [note, setNote] = useState("");
	const [expiresAt, setExpiresAt] = useState("");

	const mutation = useMutation(
		orpc.grantMinutes.mutationOptions({
			onSuccess: async (row) => {
				await Promise.all([
					queryClient.invalidateQueries({ queryKey: orpc.getTeam.key() }),
					queryClient.invalidateQueries({ queryKey: orpc.listTeams.key() }),
				]);
				toast.success(`Assigned ${row.minutes.toLocaleString()} minutes to ${teamName}`);
				onClose();
			},
			onError: (err) => toast.error(err.message || "Failed to assign minutes"),
		}),
	);

	const n = Number(minutes);
	const valid = Number.isInteger(n) && n > 0;

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!valid) return;
		mutation.mutate({
			teamId,
			minutes: n,
			reference: reference.trim() || null,
			note: note.trim() || null,
			expiresAt: expiresAt ? dayjs(expiresAt).endOf("day").valueOf() : null,
		});
	};

	return (
		<Dialog open={open} onClose={mutation.isPending ? undefined : onClose} fullWidth maxWidth="xs">
			<form onSubmit={submit} noValidate>
				<DialogTitle sx={{ fontWeight: 700 }}>Assign minutes to {teamName}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<TextField
							autoFocus
							label="Minutes purchased"
							type="number"
							value={minutes}
							onChange={(e) => setMinutes(e.target.value)}
							required
							fullWidth
							slotProps={{ htmlInput: { min: 1, step: 1, inputMode: "numeric" } }}
							helperText="Whole minutes of live service"
						/>
						<TextField
							label="Invoice / order reference"
							value={reference}
							onChange={(e) => setReference(e.target.value)}
							fullWidth
							placeholder="INV-2026-0042"
						/>
						<TextField
							label="Expires on"
							type="date"
							value={expiresAt}
							onChange={(e) => setExpiresAt(e.target.value)}
							fullWidth
							slotProps={{ inputLabel: { shrink: true } }}
							helperText="Optional. Shown to the team; not enforced automatically yet."
						/>
						<TextField
							label="Note"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							fullWidth
							multiline
							minRows={2}
							placeholder="e.g. Annual package, 2 events"
						/>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={onClose} color="inherit" disabled={mutation.isPending} sx={{ minHeight: 44 }}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!valid || mutation.isPending} sx={{ minHeight: 44 }}>
						{mutation.isPending ? "Saving…" : "Assign minutes"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
